// src/data/boneData.js

export const boneData = {
  human: {
    description: "Humans have 206 bones in adulthood, including the skull, spine, ribs, arms, and legs. Bones provide structure, protect organs, anchor muscles, and store calcium.",
    keyBones: [
      { name: "Skull", function: "Protects the brain and forms the head structure" },
      { name: "Femur", function: "Longest bone, supports leg and body weight" },
      { name: "Spine (Vertebral Column)", function: "Supports body, protects spinal cord, enables movement" },
      { name: "Ribs", function: "Protects heart and lungs" },
      { name: "Humerus", function: "Upper arm bone, enables arm movement" },
      { name: "Pelvis", function: "Supports abdominal organs, anchors leg muscles" },
      { name: "Tibia", function: "Main lower leg bone, supports weight" },
      { name: "Clavicle (Collarbone)", function: "Connects arm to body, stabilizes shoulder" },
      { name: "Scapula (Shoulder blade)", function: "Enables arm movement" },
      { name: "Patella (Kneecap)", function: "Protects knee joint" }
    ]
  },
  animal: {
    description: "Animals have diverse skeletal structures depending on species. Mammals, reptiles, and others have unique bone arrangements for movement, protection, and adaptation.",
    examples: [
      { species: "Dog", bones: 319 },
      { species: "Cat", bones: 230 },
      { species: "Horse", bones: 205 },
      { species: "Elephant", bones: 326 },
      { species: "Rabbit", bones: 212 }
    ],
    notableFeatures: [
      "Mammals have differentiated teeth and flexible spines",
      "Some animals have specialized bones (e.g., antlers, horns)"
    ]
  },
  bird: {
    description: "Birds have lightweight, hollow bones to aid in flight. They have a fused collarbone called the furcula (wishbone) and a keel for muscle attachment.",
    keyBones: [
      { name: "Furcula (Wishbone)", function: "Strengthens thoracic skeleton for flight" },
      { name: "Keel (Sternum)", function: "Attachment for flight muscles" },
      { name: "Humerus", function: "Wing bone, supports flight" },
      { name: "Tibiotarsus", function: "Main leg bone for walking and landing" },
      { name: "Skull", function: "Protects brain" }
    ],
    notableFeatures: [
      "Bird bones are pneumatic (air-filled) for reduced weight",
      "Fused bones provide stability during flight"
    ]
  },
  amphibian: {
    description: "Amphibians have bones adapted for both aquatic and terrestrial life. Their skeletons are generally less dense and more flexible.",
    keyBones: [
      { name: "Skull", function: "Protects brain" },
      { name: "Vertebrae", function: "Flexible spine for movement" },
      { name: "Pelvis", function: "Supports hind limbs for jumping" },
      { name: "Humerus", function: "Forelimb bone for movement" }
    ],
    notableFeatures: [
      "Amphibians have fewer bones than mammals",
      "Bones are adapted for swimming and jumping"
    ]
  },
  reptile: {
    description: "Reptiles have strong, dense bones for support and protection. Their skeletons are adapted for crawling, climbing, and sometimes swimming.",
    keyBones: [
      { name: "Skull", function: "Protects brain and sensory organs" },
      { name: "Vertebrae", function: "Supports body, enables movement" },
      { name: "Ribs", function: "Protects internal organs" },
      { name: "Limbs", function: "Adapted for crawling or climbing" }
    ],
    notableFeatures: [
      "Reptile bones are more mineralized than amphibians",
      "Some reptiles have shells (e.g., turtles)"
    ]
  },
  fish: {
    description: "Fish have skeletons adapted for swimming. Most have bony skeletons, but some (like sharks) have cartilage skeletons.",
    keyBones: [
      { name: "Skull", function: "Protects brain and sensory organs" },
      { name: "Vertebrae", function: "Flexible spine for swimming" },
      { name: "Fins", function: "Enable movement and balance" }
    ],
    notableFeatures: [
      "Fish bones are lightweight and flexible",
      "Cartilaginous fish (sharks, rays) have no true bones"
    ]
  }
};

// Additional healthcare patterns and mappings
export const diseaseSymptomMapping = {
  cardiovascular: {
    patterns: ["heart", "cardiac", "cardiovascular", "chest pain", "shortness of breath", "palpitations"],
    diseases: ["heart disease", "hypertension", "stroke", "heart attack"],
    symptoms: ["chest pain", "shortness of breath", "irregular heartbeat", "fatigue", "swollen legs"]
  },
  respiratory: {
    patterns: ["lung", "breathing", "cough", "wheeze", "shortness of breath"],
    diseases: ["asthma", "pneumonia", "bronchitis", "allergies"],
    symptoms: ["cough", "wheezing", "shortness of breath", "chest tightness"]
  },
  musculoskeletal: {
    patterns: ["joint", "bone", "muscle", "back pain", "arthritis", "stiffness"],
    diseases: ["arthritis", "osteoporosis", "fibromyalgia", "back pain"],
    symptoms: ["joint pain", "stiffness", "muscle pain", "bone pain", "limited mobility"]
  },
  metabolic: {
    patterns: ["diabetes", "blood sugar", "weight", "metabolism", "thyroid"],
    diseases: ["diabetes", "obesity", "hyperthyroidism", "hypothyroidism"],
    symptoms: ["increased thirst", "frequent urination", "weight changes", "fatigue"]
  },
  neurological: {
    patterns: ["headache", "migraine", "dizziness", "memory", "concentration"],
    diseases: ["migraine", "headache", "memory problems", "concussion"],
    symptoms: ["headache", "dizziness", "memory issues", "confusion", "vision problems"]
  },
  mental_health: {
    patterns: ["anxiety", "depression", "stress", "mood", "sleep", "mental health"],
    diseases: ["anxiety", "depression", "insomnia", "stress disorders"],
    symptoms: ["worry", "sadness", "sleep problems", "fatigue", "mood changes"]
  },
  digestive: {
    patterns: ["stomach", "abdomen", "nausea", "digestive", "bowel"],
    diseases: ["gastritis", "IBS", "acid reflux", "ulcers"],
    symptoms: ["stomach pain", "nausea", "bloating", "heartburn", "bowel changes"]
  },
  skin: {
    patterns: ["skin", "rash", "itchy", "dry skin", "eczema", "psoriasis"],
    diseases: ["eczema", "psoriasis", "dermatitis", "allergies"],
    symptoms: ["itchy skin", "red patches", "dry skin", "scaling", "rash"]
  }
};

// Treatment and management suggestions
export const treatmentSuggestions = {
  general: [
    "Maintain a healthy diet rich in fruits and vegetables",
    "Exercise regularly as appropriate for your condition",
    "Get adequate sleep (7-9 hours for adults)",
    "Manage stress through relaxation techniques",
    "Stay hydrated by drinking plenty of water",
    "Avoid smoking and limit alcohol consumption",
    "Take medications as prescribed by your doctor",
    "Attend regular medical checkups"
  ],
  pain_management: [
    "Apply heat or cold therapy as appropriate",
    "Gentle stretching and movement",
    "Over-the-counter pain relievers as directed",
    "Physical therapy if recommended",
    "Stress reduction techniques",
    "Maintain proper posture",
    "Use ergonomic tools and furniture"
  ],
  respiratory: [
    "Avoid known triggers and allergens",
    "Use prescribed inhalers correctly",
    "Practice breathing exercises",
    "Maintain good air quality at home",
    "Stay up to date with vaccinations",
    "Quit smoking and avoid secondhand smoke"
  ],
  mental_health: [
    "Practice mindfulness and meditation",
    "Maintain social connections",
    "Establish regular routines",
    "Engage in enjoyable activities",
    "Seek professional counseling when needed",
    "Consider support groups",
    "Limit alcohol and avoid drugs",
    "Practice good sleep hygiene"
  ]
};

// Emergency warning signs
export const emergencyWarnings = {
  immediate_911: [
    "chest pain with shortness of breath",
    "sudden severe headache",
    "difficulty breathing",
    "signs of stroke (FAST test)",
    "severe allergic reaction",
    "loss of consciousness",
    "severe bleeding",
    "thoughts of self-harm"
  ],
  urgent_care: [
    "high fever (over 103°F)",
    "persistent vomiting",
    "severe abdominal pain",
    "signs of dehydration",
    "severe pain that's getting worse",
    "symptoms that concern you"
  ]
};

// Lifestyle recommendations by condition
export const lifestyleRecommendations = {
  diabetes: [
    "Monitor blood sugar levels regularly",
    "Follow a balanced, low-sugar diet",
    "Exercise regularly to help control blood sugar",
    "Take medications as prescribed",
    "Maintain a healthy weight",
    "Regular foot care and eye exams"
  ],
  hypertension: [
    "Reduce sodium intake",
    "Maintain a healthy weight",
    "Exercise regularly",
    "Limit alcohol consumption",
    "Manage stress effectively",
    "Take blood pressure medications as prescribed"
  ],
  arthritis: [
    "Stay physically active with low-impact exercises",
    "Maintain a healthy weight to reduce joint stress",
    "Use heat and cold therapy for pain relief",
    "Consider physical therapy",
    "Protect joints during daily activities"
  ],
  anxiety: [
    "Practice deep breathing exercises",
    "Regular physical activity",
    "Limit caffeine intake",
    "Maintain a regular sleep schedule",
    "Consider meditation or yoga",
    "Seek professional help when needed"
  ]
};
